import { renderToStaticMarkup } from 'react-dom/server';
import styled from 'styled-components';
import { gridConfig } from '../../fuse-responsive-grid/src/grid';
import styleConfig from './styleConfig';

const mainViewPortStyles = (size) =>
	size
		? `
      ${size.borderRadius ? `border-radius: ${size.borderRadius}` : ''};
      ${size.padding ? `padding: ${size.padding}` : ''};
      ${size.fontSize ? `font-size: ${size.fontSize}` : ''};
      ${size.lineHeight ? `line-height: ${size.lineHeight}` : ''};
    `
		: '';

const hoverBoxShadowStyles = (size) => `
  box-shadow: ${size?.boxShadow || 'var(--elevation-shadow-s)'};
`;

const focusBoxShadowStyles = (size) => `
  box-shadow: inset 0 0 0 2px var(--color-global-white),
  ${size?.boxShadow || ''};
`;

const beforeElementStyles = (size) =>
	size?.iconSize
		? `
      width: ${size.iconSize};
      height: ${size.iconSize};
    `
		: '';

const iconRightStyles = (size) =>
	size
		? `
      ${size.padding ? `padding: ${size.padding}` : ''};
      ${size.sidePadding ? `padding-right: ${size.sidePadding}` : ''};
    `
		: '';

const iconPaddingStyles = (size) => (size?.sidePadding ? `padding-left: ${size.sidePadding};` : '');

const RenderedIcon = (Icon) => renderToStaticMarkup(Icon);

const getViewPortSize = (size, callback) =>
	typeof size === 'string' || size instanceof String || size == null
		? callback(styleConfig[size])
		: ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl']
			.filter((viewport) => size[viewport])
			.map(
				(viewport) => `
            @media screen and (min-width: ${gridConfig[viewport].minWidth}) {
              ${callback(styleConfig[size[viewport]])}
            }
          `,
			)
			.join('');

const StyledButton = styled.button
	.withConfig({
		shouldForwardProp: (prop) =>
			!['icon', 'variant', 'fullWidth', 'iconRight', 'dataTestId'].includes(prop),
	})
	.attrs({
		className: 'fuse-styled-button',
	})`
	${({ fullWidth, icon, iconRight, size, variant }) => `
      border: ${styleConfig[variant]?.border || '0'};
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background-color: ${styleConfig[variant]?.background || `var(--color-universal-greenMid)`};
      color: ${styleConfig[variant]?.color || `var(--color-global-white)`};
      position: relative;
      text-align: center;
      text-decoration: none;
      width: ${fullWidth ? '100%' : 'inherit'};
      font-weight: var(--font-weight-heading);
      ${getViewPortSize(size, mainViewPortStyles)}

      ${
	styleConfig[variant]?.hover?.border
		? `&:hover {
            border: ${styleConfig[variant]?.hover?.border};
          }`
		: ``
}

      &:hover {
        cursor: pointer;
        ${
	styleConfig[variant]?.hover?.boxShadow
		? `box-shadow: ${styleConfig[variant].hover.boxShadow}`
		: getViewPortSize(size, hoverBoxShadowStyles)
};
      }

      &:hover,
      &:focus {
        background-color: ${
	styleConfig[variant]?.hover?.background || `var(--color-universal-greenDark)`
};
      }

      &:focus {
        ${getViewPortSize(size, focusBoxShadowStyles)}
        outline: 2px solid var(--color-functional-focus);

        ${
	styleConfig[variant]?.focus?.border
		? `border: ${styleConfig[variant]?.focus?.border};`
		: ``
}
      }

      &:active {
        box-shadow: inset 0 0 0 2px var(--color-global-white);
        color: ${styleConfig[variant]?.active?.color || `var(--color-global-white)`};
        background-color: ${
	styleConfig[variant]?.active?.background || `var(--color-global-black)`
};
      }

      &:disabled {
        background-color: ${
	styleConfig[variant]?.disabled?.background || `var(--color-global-grey400)`
};
        ${styleConfig[variant]?.disabled?.color ? `color: var(--color-global-grey400)` : ``};
        ${
	styleConfig[variant]?.disabled?.border
		? `border: ${styleConfig[variant]?.disabled?.border}`
		: ``
};
        box-shadow: none;
      }

      &:disabled:hover {
        cursor: auto;
      }

      /* invisible pseudo element to make the tappable area on buttons at least 44x44px */
      &:after {
          content: '';
          display: block;
          min-height: 44px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: 44px;
          width: 100%;
      }

      ${
	icon
		? `
            ${getViewPortSize(size, iconPaddingStyles)}
            
            &:before {
              content: '';
              ${getViewPortSize(size, beforeElementStyles)}
              display: inline-flex;
              margin: 0 var(--spacing-global-space2) 0 0;
              mask: url("data:image/svg+xml,${encodeURIComponent(
		RenderedIcon(icon),
	)}") center/contain no-repeat;
              -webkit-mask: url("data:image/svg+xml,${encodeURIComponent(
		RenderedIcon(icon),
	)}") center/contain no-repeat;
              background: ${styleConfig[variant]?.color || `var(--color-global-white)`};
              ${
	iconRight
		? `
                      order: 1;
                      margin: 0 0 0 var(--spacing-global-space2);
                  `
		: ``
}
            }

            &:active:before {
              background: ${styleConfig[variant]?.active?.color || `var(--color-global-white)`};
            }

            &:disabled:before {
              ${
	styleConfig[variant]?.disabled?.color
		? `background: var(--color-global-grey400)`
		: ``
};
            }

            ${iconRight ? getViewPortSize(size, iconRightStyles) : ``}
          `
		: ``
}
  `};
`;

export default StyledButton;
